import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { useTabState } from '../../lib/tabParam';
import { Badge, Button } from '../../atoms';
import { Tabs, EntityNotesPanel, EntityActivityFeed, EntityGroupsPanel, CustomFieldsPanel } from '../../organisms';
import { EntityHeader } from '../../shell';
import { useLawEdition } from '../useLawEdition';
import { type Matter, statusVariant } from './MattersIndex';

interface DocRow { id: string; name: string | null; document_type: string | null; created_at: string | null; }

function extractRows(data: unknown): DocRow[] {
  if (Array.isArray(data)) return data as DocRow[];
  const d = data as { data?: DocRow[]; rows?: DocRow[]; documents?: DocRow[] } | null;
  return d?.data ?? d?.rows ?? d?.documents ?? [];
}

const muted = { color: 'var(--color-ink-3)' } as const;

function StatTile({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border)', borderRadius: 'var(--r, 10px)', padding: 'var(--sp-3) var(--sp-4)' }}>
      <div style={{ fontSize: 'var(--fs-xs)', ...muted, fontWeight: 'var(--fw-medium)' }}>{label}</div>
      <div style={{ fontSize: 'var(--fs-2xl, 22px)', fontWeight: 'var(--fw-semibold)', letterSpacing: 'var(--ls-tight)', marginTop: 4 }}>{value}</div>
    </div>
  );
}

/** The tabbed matter page. One master-chart fetch supplies the header + Properties; Notes and
 *  Activity reuse the existing per-entity panels (entityType="matters"), and Documents lists the
 *  documents filed to this matter. Docket/Settlements are reserved for later law phases. */
export function MatterDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { mattersOn } = useLawEdition();
  const [tab, setTab] = useTabState(['overview', 'props', 'notes', 'documents', 'groups', 'custom', 'activity'] as const, 'overview');

  const q = useQuery({
    queryKey: ['law-master-chart'],
    queryFn: ({ signal }) => api.get<{ matters: Matter[] }>('/api/law/master-chart', signal),
    enabled: mattersOn,
    retry: false,
  });
  const docsQ = useQuery({
    queryKey: ['law-matter-docs', id],
    queryFn: ({ signal }) => api.get<unknown>(`/api/documents?matter_id=${encodeURIComponent(id)}`, signal),
    enabled: mattersOn && !!id,
  });

  if (!mattersOn) return <div style={{ padding: 'var(--sp-6)', ...muted }}>Not available for your firm.</div>;
  if (q.isLoading) return <div style={{ padding: 'var(--sp-6)', ...muted }}>Loading…</div>;

  const matter = (q.data?.matters ?? []).find((m) => m.id === id);
  if (!matter) {
    return (
      <div style={{ padding: 'var(--sp-6)', ...muted }}>
        Matter not found. <Link to="/law/matters" style={{ color: 'var(--brand-primary)' }}>Back to Matters</Link>
      </div>
    );
  }

  const props = matter.properties ?? [];
  const docs = extractRows(docsQ.data);

  return (
    <div>
      <EntityHeader
        prefix={matter.case_no ? `Matter · ${matter.case_no}` : 'Matter'}
        title={matter.name}
        subtitle={matter.client_name}
        statusPills={<Badge variant={statusVariant(matter.status)}>{matter.status || '—'}</Badge>}
        actions={<Button variant="secondary" onClick={() => navigate('/law/matters')}>Back</Button>}
        kpis={<>
          <StatTile label="Properties" value={String(matter.property_count)} />
          <StatTile label="Property appeals" value={String(props.length)} />
          <StatTile label="Documents" value={String(docs.length)} />
          <StatTile label="Status" value={matter.status || '—'} />
        </>}
      />

      <Tabs
        activeValue={tab}
        onChange={(v) => setTab(v as typeof tab)}
        items={[
          { value: 'overview', label: 'Overview' },
          { value: 'props', label: 'Properties & Appeals', count: props.length },
          { value: 'notes', label: 'Notes' },
          { value: 'documents', label: 'Documents', count: docs.length },
          { value: 'groups', label: 'Groups' },
          { value: 'custom', label: 'Custom Fields' },
          { value: 'activity', label: 'Activity' },
          { value: 'docket', label: 'Docket', disabled: true },
          { value: 'settlements', label: 'Settlements', disabled: true },
        ]}
      />

      <div style={{ paddingTop: 'var(--sp-5)' }}>
        {tab === 'overview' && (
          <div style={{ background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border)', borderRadius: 'var(--r, 10px)', padding: 'var(--sp-4) var(--sp-5)', maxWidth: 560 }}>
            <dl style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 'var(--sp-2) var(--sp-4)', margin: 0, fontSize: 'var(--fs-md)' }}>
              <dt style={muted}>Client</dt><dd style={{ margin: 0 }}>{matter.client_name}</dd>
              <dt style={muted}>Case number</dt><dd style={{ margin: 0, fontFamily: 'var(--font-mono)' }}>{matter.case_no || '—'}</dd>
              <dt style={muted}>Docket</dt><dd style={{ margin: 0, fontFamily: 'var(--font-mono)' }}>{matter.docket_no || '—'}</dd>
              <dt style={muted}>Status</dt><dd style={{ margin: 0 }}><Badge variant={statusVariant(matter.status)}>{matter.status || '—'}</Badge></dd>
              <dt style={muted}>Properties</dt><dd style={{ margin: 0 }}>{matter.property_count}</dd>
            </dl>
          </div>
        )}

        {tab === 'props' && (
          <SimpleTable
            empty="No property appeals attached to this matter yet."
            head={['Property', 'Year', 'Docket']}
            rows={props.map((p) => [p.property_name, String(p.tax_year), p.docket_no || '—'])}
          />
        )}

        {tab === 'notes' && <EntityNotesPanel entityType="matters" entityId={id} />}

        {tab === 'groups' && <EntityGroupsPanel entityType="matter" entityId={id} />}

        {tab === 'custom' && <CustomFieldsPanel entityType="matters" entityId={id} />}

        {tab === 'documents' && (
          <SimpleTable
            empty="No documents filed to this matter yet."
            head={['Document', 'Type', 'Added']}
            rows={docs.map((d) => [d.name || '—', d.document_type || '—', (d.created_at || '').slice(0, 10) || '—'])}
          />
        )}

        {tab === 'activity' && <EntityActivityFeed entityType="matters" entityId={id} />}
      </div>
    </div>
  );
}

function SimpleTable({ head, rows, empty }: { head: string[]; rows: string[][]; empty: string }) {
  if (!rows.length) return <div style={{ ...muted, padding: 'var(--sp-2)' }}>{empty}</div>;
  return (
    <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--r, 10px)', overflow: 'hidden', background: 'var(--color-surface, #fff)' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-sm, 13px)' }}>
        <thead>
          <tr>{head.map((h) => (
            <th scope="col" key={h} style={{ textAlign: 'left', padding: 'var(--sp-2) var(--sp-4)', fontSize: 'var(--fs-xs)', textTransform: 'uppercase', letterSpacing: '0.04em', ...muted, borderBottom: '1px solid var(--color-border)' }}>{h}</th>
          ))}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>{r.map((c, j) => (
              <td key={j} style={{ padding: 'var(--sp-2) var(--sp-4)', borderBottom: '1px solid var(--color-border)' }}>{c}</td>
            ))}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
